import { ContentLoading } from "../components/ContentLoading";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LockKeyhole, Settings2 } from "lucide-react";
import { useState } from "react";
import type { z } from "zod";
import type {
  OpsDocEmployeePrincipal,
  OpsDocGroup,
  OpsDocSpace,
} from "../../shared/operations-docs-schemas";
import type {
  OpsDocSpaceAccessResponseSchema,
  OpsDocSpaceAccessUpdate,
} from "../../shared/operations-docs-maintenance";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { getDocSpaceAccess, updateDocSpaceAccess } from "./docs-maintenance-api";
import { RestrictedSubjects } from "./DocsShareDialog";

type Props = { space: OpsDocSpace; employees: OpsDocEmployeePrincipal[]; groups: OpsDocGroup[] };
function useSpaceAccessMutation(
  space: OpsDocSpace,
  input: Omit<OpsDocSpaceAccessUpdate, "expectedRevision">,
  onSaved: () => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      updateDocSpaceAccess(space.id, {
        ...input,
        entries: input.accessMode === "restricted" ? input.entries : [],
        expectedRevision: space.revision,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ops", "docs"] });
      onSaved();
    },
  });
}
function SpaceAccessForm({
  data,
  employees,
  groups,
  onSaved,
}: Omit<Props, "space"> & {
  data: z.infer<typeof OpsDocSpaceAccessResponseSchema>;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState(data.space.accessMode);
  const [level, setLevel] = useState(data.space.everyoneAccessLevel);
  const [entries, setEntries] = useState<OpsDocSpaceAccessUpdate["entries"]>(
    data.entries.map(({ subjectType, subjectId, accessLevel }) => ({
      subjectType,
      subjectId,
      accessLevel,
    })),
  );
  const action = useSpaceAccessMutation(
    data.space,
    { accessMode: mode, everyoneAccessLevel: level, entries },
    onSaved,
  );
  return (
    <>
      <DialogBody>
        <SpaceMode mode={mode} setMode={setMode} />
        {mode === "everyone" ? (
          <label className="grid gap-2 text-sm font-semibold">
            Everyone can
            <select
              className="min-h-11 rounded-md border border-input bg-card px-3 text-base font-normal"
              value={level}
              onChange={(event) => {
                setLevel(event.target.value === "edit" ? "edit" : "view");
              }}
            >
              <option value="view">View</option>
              <option value="edit">Edit</option>
            </select>
          </label>
        ) : (
          <RestrictedSubjects
            employees={employees}
            groups={groups}
            entries={entries}
            ownerEmployeeId={data.space.ownerEmployeeId}
            updateEntry={(subjectType, subjectId, entry) => {
              setEntries((current) => [
                ...current.filter(
                  (row) => row.subjectType !== subjectType || row.subjectId !== subjectId,
                ),
                ...(entry ? [entry] : []),
              ]);
            }}
          />
        )}
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <LockKeyhole className="size-4 shrink-0" />
          The space creator keeps management access. Page sharing can further restrict who can open
          each document.
        </p>
        {action.error && <FieldError>{action.error.message}</FieldError>}
      </DialogBody>
      <DialogFooter>
        <Button
          disabled={action.isPending}
          onClick={() => {
            action.mutate();
          }}
        >
          {action.isPending ? "Saving…" : "Save space access"}
        </Button>
      </DialogFooter>
    </>
  );
}
function SpaceMode({
  mode,
  setMode,
}: {
  mode: "everyone" | "restricted";
  setMode: (value: "everyone" | "restricted") => void;
}) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      Who can access this space
      <select
        className="min-h-10 rounded-sm border bg-card px-3 font-normal"
        value={mode}
        onChange={(event) => {
          setMode(event.target.value === "everyone" ? "everyone" : "restricted");
        }}
      >
        <option value="restricted">Selected people and groups</option>
        <option value="everyone">Everyone with Docs access</option>
      </select>
    </label>
  );
}
export function DocsSpaceAccessDialog(props: Props) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        aria-label={`Access to ${props.space.name}`}
        title="Space access"
        render={<Button size="icon-sm" variant="ghost" />}
      >
        <Settings2 />
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Access to {props.space.name}</DialogTitle>
          <DialogDescription>
            Control access to this space and the documents inside it.
          </DialogDescription>
        </DialogHeader>
        {open && (
          <SpaceAccessContent
            {...props}
            onSaved={() => {
              setOpen(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SpaceAccessContent(props: Props & { onSaved: () => void }) {
  const query = useQuery({
    queryKey: ["ops", "docs", "space", props.space.id, "access"],
    queryFn: () => getDocSpaceAccess(props.space.id),
    staleTime: 0,
  });
  return (
    <>
      {query.isPending || query.error ? (
        <DialogBody>
          {query.isPending && <ContentLoading label="Loading space access…" />}
          {query.error && <FieldError>{query.error.message}</FieldError>}
        </DialogBody>
      ) : null}
      {query.isPending || query.error ? (
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />}>Cancel</DialogClose>
        </DialogFooter>
      ) : null}
      {query.data && (
        <SpaceAccessForm
          key={`${query.data.space.id}:${query.data.space.revision}`}
          data={query.data}
          employees={props.employees}
          groups={props.groups}
          onSaved={() => {
            props.onSaved();
          }}
        />
      )}
    </>
  );
}
